import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalState } from "../contexts/GlobalContext";
import { Link } from "react-router-dom";

function Contacts() {
  const { state, fetchContacts } = useGlobalState();
  const contacts = state?.contacts || [];
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [phoneHidden, setPhoneHidden] = useState(true);
  const [hoveredPhone, setHoveredPhone] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const CONTACT_TABS = [
    { id: "all", label: "All" },
    { id: "contacts", label: "Contacts" },
    { id: "prospects", label: "Prospects" },
    { id: "investor", label: "Investor" },
    { id: "high_priority", label: "High Priority" },
  ];

  useEffect(() => {
    if (state.user?.id && state.contacts.length === 0) {
      fetchContacts();
    }
  }, [state.user]);

  if (!state || !state.contacts) {
    console.error("Contacts not found in Global Context");
    return <p>Error: No contacts available.</p>;
  }

  const handleSort = (field) => {
    setCurrentPage(1);
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortOrder(field === "createdAt" ? "desc" : "asc");
  };

  function formatAddress(contact = {}) {
    const mailingParts = [contact.mailAddress, contact.mailCity, contact.mailSt, contact.mailZip]
      .filter((part) => part && String(part).trim());
    const mailingAddress = mailingParts.join(', ');

    if (mailingAddress) return mailingAddress;
    return contact.interestAddress || '';
  }

  function formatNotes(notes) {
    if (!notes) return '';
    const trimmed = notes.trim();
    if (trimmed.length <= 120) return trimmed;
    return `${trimmed.slice(0, 117)}...`;
  }

  function getSearchableName(contact = {}) {
    const firstName = String(contact.first_name || "").trim();
    const lastName = String(contact.last_name || "").trim();
    return [contact.name, firstName, lastName, `${firstName} ${lastName}`]
      .filter((value) => value && String(value).trim())
      .join(" ");
  }

  const filteredContacts = contacts
    .filter((contact) => {
      if (activeTab === "contacts") return contact.role === "contact";
      if (activeTab === "prospects") return contact.role === "prospect";
      if (activeTab === "investor") return contact.role === "investor";
      if (activeTab === "high_priority") {
        return String(contact.priority || "").toLowerCase() === "high";
      }
      return true;
    })
     .filter((contact) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const phoneDigits = (contact.phone || "").replace(/\D/g, "");
      const queryDigits = searchQuery.replace(/\D/g, "");
      const searchableName = getSearchableName(contact).toLowerCase();
      
      const matchesPhone =
        queryDigits.length > 0 && phoneDigits.includes(queryDigits);

      const matchesBasic =
        searchableName.includes(query) ||
        (contact.email && contact.email.toLowerCase().includes(query)) ||
        matchesPhone;

      const companyFields = [contact.company_name, contact.llcs, contact.companyName].filter(
        (field) => field && String(field).trim()
      );

      const matchesCompany = companyFields.some((field) =>
        field.toLowerCase().includes(query)
      );

      const propertyFields = [];
      if (contact.interestAddress) propertyFields.push(contact.interestAddress);
      if (Array.isArray(contact.propertyType)) {
        propertyFields.push(contact.propertyType.join(" "));
      } else if (contact.propertyType) {
        propertyFields.push(contact.propertyType);
      }

      if (Array.isArray(contact.properties)) {
        contact.properties.forEach((prop) => {
          const addr = `${prop.streetAddress || ""} ${prop.city || ""} ${prop.state || ""} ${prop.zipCode || ""}`.trim();
          if (addr) propertyFields.push(addr);
        });
      }

      const matchesProperties = propertyFields.some((field) =>
        field.toLowerCase().includes(query)
      );

      const address = formatAddress(contact);
      const matchesAddress = address && address.toLowerCase().includes(query);

      return matchesBasic || matchesCompany || matchesProperties || matchesAddress;
    })
    .sort((a, b) => {
      if (sortField === "createdAt") {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
      }

      const nameA = a.name?.toLowerCase() || "";
      const nameB = b.name?.toLowerCase() || "";
      return sortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleContactClick = (contact) => {
    if (!contact || !contact.id) {
      console.error("Invalid contact data:", contact);
      return;
    }
    navigate(`/contact/${contact.id}`);
  };

  const handlePerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const maskPhone = (phone) => {
    if (!phone) return "";

    // Remove non-digit characters
    const digits = phone.replace(/\D/g, "");

    if (digits.length !== 10) return "(***) ***-****";

    return "(***) ***-****";
  };


  return (
    <div className="container mt-3">
      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {CONTACT_TABS.map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => {
                setActiveTab(tab.id);
                setCurrentPage(1);
              }}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>Contacts</h2>
        <div>
          <label className="me-2">Show</label>
          <select value={itemsPerPage} onChange={handlePerPageChange} className="form-select d-inline-block w-auto">
            {[10, 15, 25, 30, 40, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          <span className="ms-2">per page</span>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <Link className="btn btn-primary mb-2" to="/form" state={{ role: "contact" }}>
        + Add Contact
      </Link>

      <table className="table table-striped table-sm">
        <thead>
          <tr>
            <th style={{ cursor: "pointer" }} onClick={() => handleSort("name")}>
              Name {sortField === "name" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </th>
            <th style={{ cursor: "pointer" }} onClick={() => setPhoneHidden((prev) => !prev)}>
              Phone {phoneHidden ? "🙈" : "👁️"}
            </th>
            <th>Notes</th>
            <th>Priority</th>
            <th>Address</th>
            <th style={{ cursor: "pointer" }} onClick={() => handleSort("createdAt")}>
              Created {sortField === "createdAt" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </th>
          </tr>
        </thead>
        <tbody style={{ minHeight: "300px" }}>
          {paginatedContacts.map((contact) => {
            const address = formatAddress(contact);
            const notesPreview = formatNotes(contact.notes);

            return (
              <tr key={contact.id} onClick={() => handleContactClick(contact)} style={{ cursor: "pointer" }}>
                <td>{contact.name}</td>
                <td
                  onMouseEnter={() => setHoveredPhone(contact.id)}
                  onMouseLeave={() => setHoveredPhone(null)}
                  >
                  {phoneHidden && hoveredPhone !== contact.id
                    ? maskPhone(contact.phone)
                    : contact.phone}
                </td>
                <td>{notesPreview}</td>
                <td>{contact.priority || "—"}</td>
                <td>{address}</td>
                <td>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : "—"}</td>
              </tr>
            );
          })}

          {/* Add blank rows to pad to itemsPerPage */}
          {Array.from({ length: itemsPerPage - paginatedContacts.length }, (_, i) => (
            <tr key={`pad-${i}`} style={{ visibility: "hidden" }}>
              <td>padding</td>
              <td>padding</td>
              <td>padding</td>
              <td>padding</td>
              <td>padding</td>
              <td>padding</td>
            </tr>
          ))}

          {paginatedContacts.length === 0 && (
            <tr>
              <td colSpan="6" className="text-center">No contacts found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center">
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <div>
          <button
            className="btn btn-outline-secondary btn-sm me-2"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default Contacts;
